/* eslint-disable @typescript-eslint/no-explicit-any */
import { Pill } from './Pill';
import { Button, ButtonLink } from '@design-system/components/button/button';
import { Link } from 'react-router';

import { Col, Row } from '@design-system/components/grid/grid';
import { Typography } from '@design-system/components/typography/typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { bgColorsVars } from '@design-system/vars.css';
import { CellMenu } from '@client/new/components/TableShared/Cell';
import { forwardRef } from 'react';
import { ListCell, ListCellType, ListCellValue } from './ListCell';
import { Tooltip } from '@design-system/components/tooltip/tooltip';

export type ForwardedProps = {
  'data-testid'?: string;
  tooltipRef?: React.ForwardedRef<HTMLDivElement>;
  tooltipProps?: React.HTMLAttributes<HTMLDivElement>;
};

type ListCellRenderer<T extends ListCellType> = (
  props: {
    value: ListCellValue<T>;
  } & ForwardedProps,
) => React.ReactElement;

const PillRenderer: ListCellRenderer<'pill'> = ({ value, ...p }) => (
  <Col
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      height: '100%',
    }}
  >
    <Pill {...p} {...value} />
  </Col>
);

const ImageRenderer: ListCellRenderer<'image'> = ({ value, ...p }) => (
  <Col
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      height: '100%',
    }}
  >
    <img
      {...p}
      {...value}
      style={{ width: value.width, height: value.height }}
    />
  </Col>
);

const ColRenderer: ListCellRenderer<'col'> = ({ value, ...p }) => (
  <Col data-testid={p['data-testid']}>
    {value.map((v, i) => (
      <ListCellRenderer data={v} key={i} />
    ))}
  </Col>
);

const RowRenderer: ListCellRenderer<'row'> = ({ value, ...p }) => (
  <Row alignCenter data-testid={p['data-testid']} {...value.props}>
    {value.data.map((v, i) => (
      <ListCellRenderer data={v} key={i} />
    ))}
  </Row>
);

const TitleRenderer: ListCellRenderer<'title'> = ({ value, ...p }) => (
  <Typography
    data-testid={p['data-testid']}
    variant="body"
    size="medium"
    fontWeight="medium"
  >
    {value}
  </Typography>
);

const TextRenderer: ListCellRenderer<'text'> = ({ value, ...p }) => (
  <Typography
    data-testid={p['data-testid']}
    variant="body"
    fontWeight="normal"
    size="medium"
  >
    {value}
  </Typography>
);

const SmallTextRenderer: ListCellRenderer<'small-text'> = ({ value, ...p }) => (
  <Typography
    data-testid={p['data-testid']}
    variant="body"
    fontWeight="normal"
    size="small"
  >
    {value}
  </Typography>
);

const BoldTextRenderer: ListCellRenderer<'bold-text'> = ({ value, ...p }) => (
  <Typography
    data-testid={p['data-testid']}
    variant="body"
    fontWeight="bold"
    size="medium"
  >
    {value}
  </Typography>
);

const TypographyRenderer: ListCellRenderer<'typography'> = (p) => {
  const { value, ...forwardedProps } = p;
  const { content, ...typographyProps } = value;
  return (
    <Typography
      data-testid={forwardedProps['data-testid']}
      {...typographyProps}
    >
      {content}
    </Typography>
  );
};

const IconRenderer: ListCellRenderer<'icon'> = (p) => {
  const { src, ...props } = p.value;
  return (
    <FontAwesomeIcon
      style={{
        width: '14px',
        height: '14px',
        marginTop: '-1px',
        marginLeft: '3px',
        color: props.color || bgColorsVars['P-80'],
      }}
      icon={src}
      ref={p.tooltipRef as any}
      {...(p.tooltipProps as any)}
      {...props}
    />
  );
};

const TooltipRenderer: ListCellRenderer<'tooltip'> = ({ value, ...p }) => {
  const l = value.title.length;
  const maxWidth = Math.max(100, Math.min((l / 60) * 100, 400));
  return (
    <Tooltip
      variant="dark"
      title={value.title}
      arrow
      tooltipProps={{
        maxWidth,
      }}
    >
      <ListCellRenderer data={value.content} {...p} />
    </Tooltip>
  );
};

const ButtonRenderer: ListCellRenderer<'button'> = ({ value, ...p }) => (
  <Row
    data-testid={p['data-testid']}
    alignCenter
    gap="s"
    justifyCenter
    style={{ height: '100%' }}
  >
    <Button variant="text" {...value} style={{ padding: 3, borderRadius: 2 }} />
  </Row>
);

const LinkRenderer: ListCellRenderer<'link'> = ({ value, ...p }) => {
  const { content, ...props } = value;
  return (
    <Row
      data-testid={p['data-testid']}
      alignCenter
      gap="s"
      justifyCenter
      style={{ height: '100%' }}
    >
      <ButtonLink {...props}>{content}</ButtonLink>
    </Row>
  );
};

const LinkButtonRenderer: ListCellRenderer<'linkButton'> = ({
  value,
  ...p
}) => {
  const content = (
    <Row>
      {value.data.map((v, i) => (
        <ListCellRenderer data={v} key={i} />
      ))}
    </Row>
  );
  const { to } = value.props;

  const styles = {
    color: '#000',
    textDecoration: 'underline',
    fontWeight: 500,
  };

  return (
    <Row data-testid={p['data-testid']} alignCenter justifyCenter>
      <span ref={p.tooltipRef} {...p.tooltipProps}>
        {typeof to === 'string' && to.startsWith('http') ? (
          <a
            style={{ height: '100%', ...styles }}
            href={to}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {content}
          </a>
        ) : (
          <Link
            to={to}
            onClick={(e) => e.stopPropagation()}
            style={{ ...value.props.style, ...styles }}
          >
            {content}
          </Link>
        )}
      </span>
    </Row>
  );
};
const MenuRenderer: ListCellRenderer<'menu'> = ({ value, ...p }) => (
  <CellMenu items={value} data-testid={p['data-testid']} />
);

const renderers: { [T in ListCellType]: ListCellRenderer<T> } = {
  col: ColRenderer,
  row: RowRenderer,

  title: TitleRenderer,
  text: TextRenderer,
  'small-text': SmallTextRenderer,
  'bold-text': BoldTextRenderer,
  typography: TypographyRenderer,

  icon: IconRenderer,
  button: ButtonRenderer,
  link: LinkRenderer,
  linkButton: LinkButtonRenderer,

  pill: PillRenderer,
  tooltip: TooltipRenderer,
  menu: MenuRenderer,
  image: ImageRenderer,
};

export const ListCellRenderer = forwardRef<HTMLDivElement, { data: ListCell }>(
  ({ data: p, ...tooltipProps }, tooltipRef) => {
    const forwardedProps: ForwardedProps = {
      'data-testid': `list-cell-${p.type}`,
      tooltipRef,
      tooltipProps,
    };

    const Renderer = renderers[p.type] as any;
    return <Renderer value={p.value} {...forwardedProps} />;
  },
);
